// HomeScreen - Main landing page with cosmic design
import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Star, Sparkles, Gift, BookOpen, Library, Stars, Heart, ArrowRight, Compass } from 'lucide-react';
import { Button } from '../common/Button';
import { PageContainer } from '../layout/PageContainer';
import { getTodayDateString } from '../../utils/helpers';

const floatAnimation = {
    y: [0, -12, 0],
    rotate: [-1, 1, -1],
    transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

const intervalAnimation = (delay) => ({
    opacity: [0.4, 1, 0.4],
    scale: [0.95, 1.05, 0.95],
    transition: {
        duration: 3,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
    }
});

// Reusable Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, onClick, delay = 0, className = '' }) => (
    <motion.button
        className={`card-feature-glass relative group p-3 sm:p-5 rounded-xl sm:rounded-2xl text-left w-full h-full flex flex-col items-start gap-2 sm:gap-3 overflow-hidden ${className}`}
        onClick={onClick}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.5 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
    >
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="p-2 sm:p-2.5 rounded-lg sm:rounded-xl bg-theme-glass-bg border border-theme-glass-border group-hover:border-theme-accent/30 group-hover:bg-theme-accent/10 transition-colors">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-theme-accent group-hover:text-theme-accent-light transition-colors" />
        </div>

        <div>
            <h3 className="text-sm sm:text-lg font-serif font-semibold text-theme-primary-light group-hover:text-theme-text transition-colors">
                {title}
            </h3>
            <p className="text-[10px] sm:text-sm text-theme-text-muted leading-snug group-hover:text-theme-text-secondary transition-colors">
                {description}
            </p>
        </div>

        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-0 translate-x-2 duration-300">
            <ArrowRight className="w-4 h-4 text-theme-accent" />
        </div>
    </motion.button>
);

// Hero Card for Main Action
const HeroCard = ({ onClick, onDrawOrViewDailyCard, currentUser, dailyCardData, isDailyCardLoading }) => (
    <motion.div
        className="w-full h-full"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
    >
        <button
            onClick={onClick}
            className="card-hero-glass group relative w-full h-full p-4 sm:p-8 rounded-2xl sm:rounded-3xl text-center overflow-hidden transition-all duration-500 flex flex-col items-center justify-center"
        >
            {/* Background animated mesh */}
            <div className="absolute inset-0 opacity-20 bg-gradient-to-b from-theme-primary-30 via-transparent to-transparent group-hover:opacity-30 transition-opacity duration-500" />

            <div className="relative z-10 flex flex-col items-center gap-2 sm:gap-4">
                <motion.div
                    className="relative"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                    <div className="absolute inset-0 bg-theme-accent/20 blur-xl rounded-full scale-150 animate-pulse-slow" />
                    <Compass className="w-12 h-12 sm:w-20 sm:h-20 text-theme-accent drop-shadow-[0_0_15px_var(--theme-accent-glow)]" />
                </motion.div>

                <div>
                    <h2 className="text-xl sm:text-3xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--theme-text)] via-[var(--theme-accent)] to-[var(--theme-text)] mb-1 sm:mb-2 animate-shimmer">
                        Begin Your Journey
                    </h2>
                    <p className="text-xs sm:text-base text-theme-text-muted hover:text-theme-text-secondary transition-colors max-w-xs mx-auto">
                        Ask the cards and unveil the secrets of your destiny
                    </p>
                </div>

                <div className="mt-1 sm:mt-2 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full border border-theme-accent/30 bg-theme-primary-20 text-theme-accent text-xs sm:text-sm font-medium group-hover:bg-theme-accent/10 group-hover:border-theme-accent/50 transition-all duration-300 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Start Reading</span>
                </div>
            </div>
        </button>
    </motion.div>
);

// Daily Card Banner
const DailyCardBanner = ({ onClick, currentUser, hasDrawn, isLoading }) => {
    if (!currentUser || currentUser.isAnonymous) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="w-full mt-2 sm:mt-6 opacity-60 pointer-events-none grayscale"
            >
                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-theme-glass-border bg-theme-glass-bg">
                    <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-theme-text-muted" />
                    <div className="text-left">
                        <h4 className="font-serif text-xs sm:text-sm font-medium text-theme-text-muted">Card of the Day</h4>
                        <p className="text-[10px] sm:text-xs text-theme-text-muted opacity-60">Sign in to unlock your daily wisdom</p>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.button
            onClick={onClick}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="w-full mt-2 sm:mt-6 group relative overflow-hidden flex-shrink-0"
        >
            <div className={`p-0.5 sm:p-1 rounded-xl sm:rounded-2xl bg-gradient-to-r ${hasDrawn ? 'from-theme-primary-30 to-theme-secondary' : 'from-theme-accent-30 via-theme-glow to-theme-accent-30'} border border-theme-glass-border hover:border-theme-accent/50 transition-colors duration-300`}>
                <div className="bg-[#0a0a0f]/90 backdrop-blur-md rounded-lg sm:rounded-xl p-2.5 sm:p-4 flex items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="relative">
                            <Gift className={`w-7 h-7 sm:w-10 sm:h-10 ${hasDrawn ? 'text-theme-primary-light' : 'text-theme-accent'} drop-shadow-lg`} />
                            {!hasDrawn && !isLoading && (
                                <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full shadow-glow-gold animate-pulse" />
                            )}
                        </div>
                        <div className="text-left">
                            <h4 className="font-serif text-sm sm:text-base font-bold text-theme-text group-hover:text-white transition-colors">
                                {isLoading ? 'Unveiling...' : hasDrawn ? 'Your Daily Card' : 'Card of the Day'}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-theme-text-muted group-hover:text-theme-text-secondary transition-colors">
                                {hasDrawn ? 'View your guidance for today' : 'Tap to receive your daily whisper'}
                            </p>
                        </div>
                    </div>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-theme-text-muted group-hover:text-theme-accent transform group-hover:translate-x-1 transition-all" />
                </div>
            </div>
        </motion.button>
    );
};

export const HomeScreen = ({
    onBegin,
    onPastReadings,
    onGoToCardsList,
    onGoToBirthChart,
    onGoToSynastry,
    onDrawOrViewDailyCard,
    currentUser,
    dailyCardData,
    isDailyCardLoading
}) => {
    const hasUserDrawnToday = dailyCardData?.dateDrawn === getTodayDateString();

    return (
        <PageContainer className="h-[100dvh] flex flex-col items-center justify-center gap-4 sm:gap-6 pt-2 pb-12 sm:pt-4 sm:pb-20 px-4 sm:px-6 overflow-hidden">

            {/* 1. Header Section */}
            <motion.div
                className="relative text-center z-10 flex-shrink-0"
                animate={floatAnimation}
            >
                {/* Glow ring */}
                <motion.div
                    className="absolute inset-0 -m-10 rounded-full blur-[60px]"
                    style={{ background: 'radial-gradient(circle, rgba(147, 51, 234, 0.2) 0%, transparent 70%)' }}
                    animate={intervalAnimation(0)}
                />

                {/* Main moon/orb */}
                <div className="relative mb-2 sm:mb-6 inline-block">
                    <Moon className="w-14 h-14 sm:w-24 sm:h-24 text-theme-primary-light/30" />
                    <motion.div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        animate={intervalAnimation(0.5)}
                    >
                        <Star className="w-10 h-10 sm:w-16 sm:h-16 text-theme-accent drop-shadow-[0_0_20px_var(--theme-accent-glow)]" />
                    </motion.div>
                </div>

                <motion.h1
                    className="text-2xl sm:text-5xl font-serif font-bold mb-1 sm:mb-2 tracking-wide"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.7 }}
                >
                    <span className="bg-gradient-to-r from-[var(--theme-text)] via-[var(--theme-accent-light)] to-[var(--theme-text)] bg-clip-text text-transparent drop-shadow-sm">
                        Cosmic Compass
                    </span>
                </motion.h1>

                <motion.p
                    className="text-[10px] sm:text-base text-theme-text-muted font-medium tracking-wider uppercase"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.7 }}
                >
                    Navigate life's journey through celestial wisdom
                </motion.p>
            </motion.div>

            {/* 2. Main Content Grid */}
            {/* 2. Main Content Grid - Fixed Layout */}
            <div className="w-full max-w-sm sm:max-w-md flex flex-col gap-3 sm:gap-4 z-20 my-2">

                {/* Hero / Main Action - Fixed Height ~32% of screen */}
                <div className="h-[32vh] max-h-[260px] min-h-[200px]">
                    <HeroCard onClick={onBegin} />
                </div>

                {/* Secondary Features Grid - Fixed Height ~15% of screen */}
                <div className="h-[15vh] max-h-[120px] min-h-[100px] grid grid-cols-2 gap-2 sm:gap-4">
                    <FeatureCard
                        icon={Stars}
                        title="Birth Chart"
                        description="Your celestial blueprint"
                        onClick={onGoToBirthChart}
                        delay={0.4}
                    />
                    <FeatureCard
                        icon={Heart}
                        title="Synastry"
                        description="Love & Comp."
                        onClick={onGoToSynastry}
                        delay={0.5}
                    />
                </div>

                {/* Daily Card Feature - Fixed Height */}
                <div className="min-h-[70px]">
                    <DailyCardBanner
                        onClick={onDrawOrViewDailyCard}
                        currentUser={currentUser}
                        hasDrawn={hasUserDrawnToday}
                        isLoading={isDailyCardLoading}
                    />
                </div>
            </div>

            {/* 3. Footer Navigation - Grouped closely */}
            <motion.div
                className="flex items-center gap-6 sm:gap-10 flex-shrink-0 mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.6 }}
            >
                <button
                    onClick={onPastReadings}
                    className="flex items-center gap-2 text-xs sm:text-sm text-theme-text-muted hover:text-theme-text transition-colors group"
                >
                    <BookOpen className="w-4 h-4" />
                    <span className="group-hover:underline decoration-theme-accent/50 underline-offset-4">Past Readings</span>
                </button>
                <div className="w-1 h-1 rounded-full bg-theme-primary/30" />
                <button
                    onClick={onGoToCardsList}
                    className="flex items-center gap-2 text-xs sm:text-sm text-theme-text-muted hover:text-theme-text transition-colors group"
                >
                    <Library className="w-4 h-4" />
                    <span className="group-hover:underline decoration-theme-accent/50 underline-offset-4">Card Wisdom</span>
                </button>
            </motion.div>

        </PageContainer>
    );
};

export default HomeScreen;
